// Client for extension Share / Add Users invites and the notification Accept/Reject flow.

export type InviteType = 'share_blueprint' | 'add_member'

export interface PickerUser {
  id: string
  email: string
  name: string
}

export interface ExtensionNotification {
  id: string
  type: InviteType
  definitionId: string
  displayName: string
  fromUserName: string
  fromUserEmail: string
  role: string | null
  createdAt: string
  title: string
  body: string
}

function authHeaders(token: string | null): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) h['Authorization'] = `Bearer ${token}`
  return h
}

async function readError(res: Response): Promise<string> {
  try {
    const data = (await res.json()) as { error?: string }
    if (data.error) return data.error
  } catch {
    // ignore
  }
  return `Request failed (${res.status})`
}

export async function listInviteUsers(
  apiBaseUrl: string,
  token: string | null,
): Promise<PickerUser[]> {
  const res = await fetch(`${apiBaseUrl}/api/extension-invites/users`, {
    credentials: 'include',
    headers: authHeaders(token),
  })
  if (!res.ok) throw new Error(await readError(res))
  const data = (await res.json()) as { users?: PickerUser[] }
  return data.users ?? []
}

export async function createExtensionInvites(
  apiBaseUrl: string,
  token: string | null,
  input: {
    type: InviteType
    definitionId: string
    displayName: string
    toUserEmails: string[]
  },
): Promise<{ invites: unknown[] }> {
  const res = await fetch(`${apiBaseUrl}/api/extension-invites`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(token),
    body: JSON.stringify(input),
  })
  if (!res.ok) throw new Error(await readError(res))
  return (await res.json()) as { invites: unknown[] }
}

export async function listNotifications(
  apiBaseUrl: string,
  token: string | null,
): Promise<ExtensionNotification[]> {
  const res = await fetch(`${apiBaseUrl}/api/notifications`, {
    credentials: 'include',
    headers: authHeaders(token),
  })
  if (!res.ok) throw new Error(await readError(res))
  const data = (await res.json()) as { notifications?: ExtensionNotification[] }
  return data.notifications ?? []
}

export async function acceptNotification(
  apiBaseUrl: string,
  token: string | null,
  id: string,
): Promise<{ type: InviteType; definitionId: string; displayName: string }> {
  const res = await fetch(`${apiBaseUrl}/api/notifications/${encodeURIComponent(id)}/accept`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(token),
  })
  if (!res.ok) throw new Error(await readError(res))
  return (await res.json()) as { type: InviteType; definitionId: string; displayName: string }
}

export async function rejectNotification(
  apiBaseUrl: string,
  token: string | null,
  id: string,
): Promise<void> {
  const res = await fetch(`${apiBaseUrl}/api/notifications/${encodeURIComponent(id)}/reject`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(token),
  })
  if (!res.ok) throw new Error(await readError(res))
}
